"use client";
import React, { useState, useEffect } from 'react';
import { collection, addDoc, getFirestore, query, where, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { X } from 'lucide-react';

const EmailCollectionPopup = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [currentCartDocId, setCurrentCartDocId] = useState(null);
  const firestore = getFirestore();

  // Initialize or retrieve session ID
  useEffect(() => {
    const storedSessionId = localStorage.getItem('cartSessionId');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      checkExistingCart(storedSessionId);
    } else {
      const newSessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      setSessionId(newSessionId);
      localStorage.setItem('cartSessionId', newSessionId);
    }
  }, []);

  // Check for existing cart
  const checkExistingCart = async (sid) => {
    try {
      const abandonedCartsRef = collection(firestore, "abandoned_carts");
      const q = query(abandonedCartsRef, where("sessionId", "==", sid));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setCurrentCartDocId(querySnapshot.docs[0].id);
      }
    } catch (error) {
      console.error("Error checking existing cart:", error);
    }
  };

  // Show popup after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      const hasSeenPopup = localStorage.getItem('hasSeenEmailPopup');
      if (!hasSeenPopup) {
        setShowPopup(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Save to users collection
      const userData = {
        email,
        createdAt: serverTimestamp(),
        sessionId,
        cartId: currentCartDocId || null
      };

      const usersRef = collection(firestore, 'users');
      await addDoc(usersRef, userData);

      // Update cart with email if it exists
      if (currentCartDocId) {
        const cartRef = doc(firestore, "abandoned_carts", currentCartDocId);
        await updateDoc(cartRef, {
          email,
          lastUpdated: serverTimestamp()
        });
      }

      setIsSubmitted(true);
      setEmail('');
      localStorage.setItem('hasSeenEmailPopup', 'true');

      setTimeout(() => {
        setShowPopup(false);
      }, 2000);
    } catch (error) {
      console.error('Error in submission process:', error);
      setError('Failed to save email. Please try again.');
    }
  };

  const handleClose = () => {
    setShowPopup(false);
    localStorage.setItem('hasSeenEmailPopup', 'true');
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 relative">
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <X size={20} />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Get 10% Off Your First Order
          </h2>
          <p className="text-gray-600">
            Subscribe to our newsletter and get exclusive offers!
          </p>
        </div>

        {!isSubmitted ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}
            <button
              type="submit"
              className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-md transition-colors font-medium"
            >
              Get My Discount
            </button>
          </form>
        ) : (
          <div className="text-center py-4">
            <p className="text-green-600 font-medium text-lg">
              Thank you! Your discount code will be sent to your email.
            </p>
          </div>
        )}

        <p className="text-xs text-gray-500 text-center mt-4">
          By subscribing you agree to receive marketing communications from us.
        </p>
      </div>
    </div>
  );
};

export default EmailCollectionPopup;